import { FormField, FormItem, FormLabel } from "@/components/ui/form";
import { TextField, SelectField, DateField, RadioGroup } from "@/components/form";
import { formatPhone, formatSsn } from "@/utils/format";
import { toDateInput } from "@/utils/dates";
import { onboarding } from "@/features/onboarding/api/onboarding.api";
import { applyScan, scanLabels, type ScannedField, useDocScan, type ScanOutcome, StepHeading, FileDrop, SubHeading, ScanStatus, useZipAutoFill, splitGivenNames, trimmed, OWNER_TITLES } from "@/features/onboarding/components/wizard";
import { STATE_OPTIONS, stateCode } from "@/utils/states";
import { countryOptions, optionLabel, selectCopy, useLang, useT } from "@/features/onboarding/i18n";
import { docSizeMb, isDocTooLarge, MAX_DOC_FILE_MB } from "@/utils/docFile";
import { PhotoField } from "./PhotoField";
import { type StepProps, type FormState } from "./schema";

export function OwnerStep({ form, dlFile, onDlFile, dlError, dlPreviousNote, onScanned, onBusyChange }: StepProps & { dlFile: File | null; onDlFile: (f: File | null) => void; dlError?: string | null; dlPreviousNote?: string | null; onScanned?: () => void; onBusyChange?: (busy: boolean) => void }) {
  const t = useT();
  useZipAutoFill(form.watch("ownerHomeZip"), (st, ci) => {
    const sv = { shouldValidate: form.formState.isSubmitted };
    if (st) form.setValue("ownerHomeState", st, sv);
    if (ci) form.setValue("ownerHomeCity", ci, sv);
  });
  const { lang } = useLang();
  const isUs = form.watch("ownerCitizenship") === "us";
  const c = form.control;

  const dl = useDocScan({
    reading: t("owner.reading"),
    failed: t("owner.failed"),
    sizeError: (f) => (isDocTooLarge(f) ? t("file.tooLarge", { size: docSizeMb(f), max: MAX_DOC_FILE_MB }) : null),
    onFile: onDlFile,
    onBusyChange,
    run: async (file, onlyEmpty): Promise<ScanOutcome> => {
      const d = await onboarding.scan(file, "driverLicense");
      const { first, middle } = splitGivenNames(d.firstName, d.middleName);
      const scanned: ScannedField<FormState>[] = [
        ["ownerFirstName", first, t("scan.firstName")],
        ["ownerMiddleName", middle, t("scan.middleName")],
        ["ownerLastName", trimmed(d.lastName), t("scan.lastName")],
        ["ownerDateOfBirth", toDateInput(d.dateOfBirth), t("scan.dob")],
        ["ownerHomeAddressLine1", trimmed(d.addressLine1), t("scan.address")],
        ["ownerHomeCity", trimmed(d.city), t("scan.city")],
        ["ownerHomeState", stateCode(d.state), t("scan.state")],
        ["ownerHomeZip", trimmed(d.zip), t("scan.zip")],
      ];
      const { read, applied } = applyScan(form, scanned, { shouldValidate: form.formState.isSubmitted, onlyEmpty });
      if (read.length === 0) {
        return { kind: "error", msg: t("owner.nothingRead") };
      }
      onScanned?.();
      return {
        kind: "success",
        msg: applied.length === 0 ? t("scan.kept") : t("owner.prefilled", { fields: scanLabels(applied).join(t("common.listSep")) }),
      };
    },
  });

  return (
    <div className="space-y-5">
      <StepHeading
        title={t("owner.title")}
        subtitle={t("owner.subtitle")}
      />

      <PhotoField
        label={t("owner.photoLabel")}
        required
        hint={t("owner.photoHint")}
      >
        <FileDrop prompt={t("file.prompt")} busy={dl.busy} file={dlFile} error={dlError} previousNote={dlPreviousNote} onFile={(f) => dl.pick(f, !!dlPreviousNote)} onRemove={dl.clear} />
        <ScanStatus msg={dl.msg} kind={dl.kind} onRetry={dl.retry} />
      </PhotoField>

      <FormField
        control={c}
        name="ownerCitizenship"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("owner.citizenship")}</FormLabel>
            <RadioGroup
              value={typeof field.value === "string" ? field.value : "us"}
              onChange={field.onChange}
              options={[
                { value: "us", label: t("owner.citizenUs") },
                { value: "non-us", label: t("owner.citizenNonUs") },
              ]}
            />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <TextField control={c} name="ownerFirstName" label={t("owner.firstName")} required />
        <TextField control={c} name="ownerMiddleName" label={t("owner.middle")} />
        <TextField control={c} name="ownerLastName" label={t("owner.lastName")} required />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DateField control={c} name="ownerDateOfBirth" label={t("owner.dob")} required disableFuture defaultYear={1990} />
        <SelectField control={c} name="ownerTitle" label={t("owner.jobTitle")} required {...selectCopy(t, t("owner.jobTitle"))} options={OWNER_TITLES.map((o) => [o, optionLabel(t, "title", o)])} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {isUs ? (
          <TextField control={c} name="ownerSsn" label={t("owner.ssn")} required inputMode="numeric" maxLength={11} placeholder="XXX-XX-XXXX" format={formatSsn} hint={t("owner.ssnHint")} />
        ) : (
          <>
            <TextField control={c} name="ownerPassportNumber" label={t("owner.passport")} required autoComplete="off" format={(v) => v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20)} hint={t("owner.passportHint")} />
            <SelectField control={c} name="ownerPassportCountry" label={t("owner.passportCountry")} required searchable {...selectCopy(t, t("owner.passportCountry"))} options={countryOptions(lang)} />
          </>
        )}
        <TextField control={c} name="ownerPhone" label={t("owner.phone")} required type="tel" inputMode="tel" maxLength={14} placeholder="(555) 123-4567" format={formatPhone} />
      </div>

      <SubHeading>{t("owner.home")}</SubHeading>
      <div className="space-y-4">
        <TextField control={c} name="ownerHomeAddressLine1" label={t("legal.street")} required placeholder="123 Main St" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <TextField control={c} name="ownerHomeZip" label={t("legal.zip")} required inputMode="numeric" maxLength={5} placeholder="94103" format={(v) => v.replace(/\D/g, "").slice(0, 5)} />
          <SelectField control={c} name="ownerHomeState" label={t("legal.state")} required {...selectCopy(t, t("legal.state"))} options={STATE_OPTIONS} />
          <TextField control={c} name="ownerHomeCity" label={t("legal.city")} required placeholder="San Francisco" className="col-span-2 sm:col-span-1" />
        </div>
      </div>
    </div>
  );
}
